import { useEffect, useState } from "react";
import api from "../lib/api";
import { runAiJob } from "../lib/aiJob";
import { toast } from "sonner";
import { PageHeader, Empty } from "../components/Layout";
import { Plus, Save, Archive, Trash2, Sparkles, Check } from "lucide-react";

const inp = "border border-[#DFDAD0] px-3 py-2 text-sm outline-none focus:border-[#1C2530] w-full";
const lbl = "font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50";

export default function Offerings() {
  const [items, setItems] = useState([]);
  const [kb, setKb] = useState([]);
  const [sel, setSel] = useState(null);
  const [newName, setNewName] = useState("");
  const [disc, setDisc] = useState(null); // {done, question, answered, total}
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [icpBusy, setIcpBusy] = useState(false);
  const [icpElapsed, setIcpElapsed] = useState(0);
  const deriveIcp = async () => {
    setIcpBusy(true);
    try {
      const data = await runAiJob(`/proposals/offerings/${sel.id}/icp/suggest`, undefined, setIcpElapsed);
      setSel((s) => ({ ...s, icp: data.icp }));
      toast.success(data.icp?._fetched ? "ICP suggested from your website" : "ICP suggested (website unreachable — from offering only)");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Could not derive ICP"); }
    finally { setIcpBusy(false); setIcpElapsed(0); }
  };

  const load = () => api.get("/proposals/offerings").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/kb").then((r) => setKb(r.data || [])).catch(() => {}); }, []);

  const select = async (o) => {
    setSel({ ...o }); setAnswer("");
    const r = await api.get(`/proposals/offerings/${o.id}/discovery/next`);
    setDisc(r.data);
  };

  const create = async () => {
    if (!newName.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/proposals/offerings", { name: newName.trim() });
      setNewName(""); await load(); await select(r.data);
      toast.success("Offering created — answer the discovery questions");
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/proposals/offerings/${sel.id}`, {
        name: sel.name, summary: sel.summary, positioning: sel.positioning,
        pricing_notes: sel.pricing_notes, kb_refs: sel.kb_refs || [], icp: sel.icp || null,
      });
      setSel({ ...r.data }); await load(); toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    setBusy(true);
    try { const r = await api.patch(`/proposals/offerings/${sel.id}`, { status: sel.status === "archived" ? "active" : "archived" }); setSel({ ...r.data }); await load(); toast.success(r.data.status === "archived" ? "Archived" : "Restored"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this offering permanently?")) return;
    setBusy(true);
    try { await api.delete(`/proposals/offerings/${sel.id}`); setSel(null); setDisc(null); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const saveAnswer = async () => {
    if (!answer.trim() || !disc?.question) return;
    setBusy(true);
    try {
      const r = await api.post(`/proposals/offerings/${sel.id}/discovery/answer`, { key: disc.question.key, answer });
      setSel({ ...r.data }); setAnswer("");
      const n = await api.get(`/proposals/offerings/${sel.id}/discovery/next`);
      setDisc(n.data); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleKb = (id) => {
    const cur = sel.kb_refs || [];
    setSel({ ...sel, kb_refs: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };

  return (
    <>
      <PageHeader eyebrow="PROPOSAL INTELLIGENCE" title="Offerings" hint="What you sell, one profile per offering: positioning, pricing hints and sample proposals. Proposal Intelligence needs at least one active offering."
        right={<div className="flex items-center gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="offering-new-name" placeholder="New offering name…" className="border border-[#DFDAD0] px-3 py-1.5 text-sm w-56 outline-none focus:border-[#1C2530]" />
          <button onClick={create} disabled={busy} data-testid="offering-create-btn" className="flex items-center gap-1 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase hover:bg-[#2a3644] disabled:opacity-40"><Plus className="w-3.5 h-3.5" /> Create</button>
        </div>} />
      <div className="p-8" data-testid="offerings-page">
        <p className="text-sm text-[#1C2530]/60 mb-5 max-w-2xl">Define your commercial offerings once. Answer a short discovery questionnaire and attach rate cards from the Knowledge Base — the Proposal AI grounds every client proposal in these profiles.</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <div className={lbl}>Offerings ({items.length})</div>
            {items.length === 0 && <Empty label="NO_OFFERINGS" />}
            {items.map((o) => (
              <button key={o.id} onClick={() => select(o)} data-testid={`offering-row-${o.id}`}
                className={`w-full text-left border p-3 transition-colors ${sel?.id === o.id ? "border-[#1C2530] bg-[#EFECE5]" : "border-[#DFDAD0] bg-white hover:bg-[#F5F3EE]"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#1C2530]">{o.name}</span>
                  {o.status === "archived" && <span className="font-mono text-[9px] uppercase text-[#1C2530]/40">archived</span>}
                </div>
                <div className="font-mono text-[9px] text-[#1C2530]/50 mt-1">{(o.discovery_answers || []).length}/8 discovery · {(o.kb_refs || []).length} rate cards</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {!sel ? <Empty label="SELECT_OR_CREATE_AN_OFFERING" /> : (
              <div className="border border-[#DFDAD0] bg-white p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <input value={sel.name || ""} onChange={(e) => setSel({ ...sel, name: e.target.value })} data-testid="offering-name-input" className="text-lg font-semibold text-[#1C2530] border-b border-transparent hover:border-[#DFDAD0] focus:border-[#1C2530] outline-none" />
                  <div className="flex gap-2">
                    <button onClick={archive} disabled={busy} data-testid="offering-archive-btn" className="flex items-center gap-1 border border-[#DFDAD0] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#EFECE5]"><Archive className="w-3 h-3" /> {sel.status === "archived" ? "Restore" : "Archive"}</button>
                    <button onClick={del} disabled={busy} data-testid="offering-delete-btn" className="flex items-center gap-1 border border-[#B23A2E]/40 text-[#B23A2E] px-2 py-1 font-mono text-[10px] uppercase hover:bg-[#B23A2E]/10"><Trash2 className="w-3 h-3" /> Delete</button>
                  </div>
                </div>

                {/* Discovery */}
                <div className="border border-[#DFDAD0] bg-[#F5F3EE] p-4">
                  <div className="flex items-center gap-2 mb-2"><Sparkles className="w-3.5 h-3.5 text-[#3E8F99]" /><span className={lbl}>Discovery · {disc?.answered ?? (sel.discovery_answers || []).length}/{disc?.total ?? 8}</span></div>
                  {disc && !disc.done ? (
                    <div data-testid="discovery-question">
                      <div className="text-sm text-[#1C2530] mb-2">{disc.question.question}</div>
                      <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid="discovery-answer-input" className={`${inp} h-16 resize-none`} placeholder="Type your answer…" />
                      <button onClick={saveAnswer} disabled={busy} data-testid="discovery-save-btn" className="mt-2 flex items-center gap-1 bg-[#3E8F99] text-white px-3 py-1.5 text-xs font-mono uppercase hover:bg-[#357981] disabled:opacity-40"><Check className="w-3.5 h-3.5" /> Save & next</button>
                    </div>
                  ) : (
                    <div className="font-mono text-[10px] uppercase text-[#2F7A3E]" data-testid="discovery-done">✓ Discovery complete</div>
                  )}
                  {(sel.discovery_answers || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#DFDAD0] space-y-2">
                      {(sel.discovery_answers || []).map((a) => (
                        <div key={a.key} className="text-xs"><span className="text-[#1C2530]/60">{a.question}</span><div className="text-[#1C2530]">{a.answer}</div></div>
                      ))}
                    </div>
                  )}
                </div>

                <div><label className={lbl}>Summary</label><textarea value={sel.summary || ""} onChange={(e) => setSel({ ...sel, summary: e.target.value })} data-testid="offering-summary" className={`${inp} h-16 resize-none mt-1`} /></div>
                <div><label className={lbl}>Positioning</label><textarea value={sel.positioning || ""} onChange={(e) => setSel({ ...sel, positioning: e.target.value })} data-testid="offering-positioning" className={`${inp} h-16 resize-none mt-1`} /></div>
                <div><label className={lbl}>Pricing notes</label><textarea value={sel.pricing_notes || ""} onChange={(e) => setSel({ ...sel, pricing_notes: e.target.value })} data-testid="offering-pricing" className={`${inp} h-16 resize-none mt-1`} /></div>

                <div className="border border-[#DFDAD0] bg-[#F5F3EE] p-4" data-testid="offering-icp">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[9px] uppercase tracking-wider text-[#1C2530]/50">Ideal Customer Profile · steers Lead Research targeting</div>
                    <button onClick={deriveIcp} disabled={icpBusy} data-testid="offering-icp-derive" className="font-mono text-[10px] uppercase border border-[#DFDAD0] px-2 py-1 hover:bg-[#EFECE5] disabled:opacity-40">{icpBusy ? `Analyzing… ${icpElapsed}s` : "Derive from website + offering"}</button>
                  </div>
                  {sel.icp ? (
                    <div className="space-y-2">
                      <div className="text-[11px] text-[#8a4a12]">AI-suggested — review &amp; edit before it's trusted. Nothing is used for targeting until you Save.</div>
                      <textarea value={sel.icp.summary || ""} onChange={(e) => setSel({ ...sel, icp: { ...sel.icp, summary: e.target.value } })} data-testid="offering-icp-summary" placeholder="ICP summary" className={`${inp} h-16 resize-none`} />
                      {[["industries", "Industries"], ["geographies", "Geographies"], ["buyer_titles", "Buyer titles"], ["keywords", "Search keywords"]].map(([k, lab]) => (
                        <div key={k}>
                          <label className={lbl}>{lab} (comma-separated)</label>
                          <input value={Array.isArray(sel.icp[k]) ? sel.icp[k].join(", ") : (sel.icp[k] || "")} onChange={(e) => setSel({ ...sel, icp: { ...sel.icp, [k]: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })} data-testid={`offering-icp-${k}`} className={`${inp} w-full`} />
                        </div>
                      ))}
                      <div>
                        <label className={lbl}>Company size</label>
                        <input value={sel.icp.company_size || ""} onChange={(e) => setSel({ ...sel, icp: { ...sel.icp, company_size: e.target.value } })} data-testid="offering-icp-size" className={`${inp} w-full`} />
                      </div>
                    </div>
                  ) : <div className="font-mono text-[10px] text-[#1C2530]/40">No ICP yet — derive it from your website + this offering, or leave empty.</div>}
                </div>

                <div>
                  <label className={lbl}>Rate cards / KB references</label>
                  <div className="mt-1 border border-[#DFDAD0] max-h-40 overflow-y-auto divide-y divide-[#DFDAD0]">
                    {kb.length === 0 && <div className="p-2 font-mono text-[10px] text-[#1C2530]/40">No KB entries — add rate cards in Knowledge Base.</div>}
                    {kb.map((k) => (
                      <label key={k.id} data-testid={`offering-kb-${k.id}`} className="flex items-center gap-2 p-2 text-xs cursor-pointer hover:bg-[#F5F3EE]">
                        <input type="checkbox" checked={(sel.kb_refs || []).includes(k.id)} onChange={() => toggleKb(k.id)} />
                        <span className="text-[#1C2530]">{k.title}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button onClick={save} disabled={busy} data-testid="offering-save-btn" className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-xs font-mono uppercase hover:bg-[#2a3644] disabled:opacity-40"><Save className="w-3.5 h-3.5" /> Save offering</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
